import {
  getHeadersWithUniqueVisitorId,
  ProductRewardSearchResult,
  RecentlySearchedTerm,
} from "@brandclub/common-ui";
import { HOST_DOMAIN } from "../../utils/domain";
import { graphqlFetch } from "../graphql/graphqlFetch";
import { getCommonRequestHeaders } from "../../utils/axios";
import {
  AUTHENTICATED_SEARCH_PRODUCTS,
  UNAUTHENTICATED_SEARCH_PRODUCTS,
  ProductOperation,
  LLM_SEARCH_PRODUCTS,
} from "../graphql/product";

const DOMAIN = HOST_DOMAIN + "/api-rewards";

// Types for search term auto-complete
export interface SearchTerm {
  searchScore: number;
  searchTerm: string;
}

export interface SearchTermAutoCompleteResponse {
  searchTerms?: SearchTerm[];
}

export interface SearchTermAutoCompleteRequest {
  searchText: string;
  searchScoreByCategoryId?: { [key: number]: number };
  brandId?: number;
}

// Types for product search
export interface ProductSearchFilters {
  titleInclude?: string[];
  titleExclude?: string[];
  stacklineSkus?: string[];
  stacklineSkusExclude?: string[];
  campaignTypes?: string[];
  retailPrice?: Array<{ min?: number; max?: number }>;
  reviewsRating?: { min?: number; max?: number };
  reviewsCount?: { min?: number; max?: number };
  brandIds?: number[];
  departmentIds?: number[];
  categoryIds?: number[];
  subCategoryIds?: number[];
  retailerIds?: number[];
  isBrandClubReady?: boolean;
  totalRewardAmount?: { min?: number; max?: number };
  imageData?: string;
  imageMimeType?: string;
}

export interface ProductSearchRequest {
  filters?: ProductSearchFilters;
  page?: {
    start: number;
    size: number;
  };
  sort?: Array<{
    fieldName: string;
    direction: "asc" | "desc";
  }>;
  facetFieldNames?: string[];
  computeFacets?: boolean;
  applySpellingCheck?: boolean;
  skipAvailabilityCheck?: boolean;
  treatment?: {
    search: string;
  };
}

export interface ProductSearchResponse {
  products?: ProductRewardSearchResult[];
  totalProducts?: number;
  facets?: any;
  suggestedTerm?: string;
  hasMore?: boolean;
  summaryText?: string;
}

/**
 * Fetches search term auto-complete suggestions
 * @param request The search term auto-complete request
 * @param signal Optional AbortSignal for request cancellation
 * @returns Promise with search term suggestions
 */
export const fetchSearchTermAutoCompleteSuggestions = async (
  request: SearchTermAutoCompleteRequest,
  signal?: AbortSignal
): Promise<SearchTermAutoCompleteResponse> => {
  const headers = {
    ...(await getHeadersWithUniqueVisitorId()),
    ...getCommonRequestHeaders(),
  };

  const res = await fetch(
    DOMAIN + `/product/searchTermAutoCompleteSuggestions`,
    {
      method: "POST",
      headers,
      body: JSON.stringify({
        searchText: request.searchText,
        searchScoreByCategoryId: request.searchScoreByCategoryId ?? {},
        brandId: request.brandId,
      }),
      signal,
    }
  );

  if (res.ok) {
    return await res.json();
  } else {
    throw new Error("Error fetching search term auto-complete suggestions");
  }
};

/**
 * Searches for products using GraphQL
 * @param request The product search request
 * @param isLoggedIn Whether the user is authenticated
 * @param signal Optional AbortSignal for request cancellation
 * @returns Promise with product search results
 */
export const searchProducts = async (
  request: ProductSearchRequest,
  isLoggedIn: boolean = false,
  isSemanticProductSearch: boolean = false,
  signal?: AbortSignal
): Promise<ProductSearchResponse> => {
  try {
    const SearchQuery = isSemanticProductSearch
      ? LLM_SEARCH_PRODUCTS
      : isLoggedIn
      ? AUTHENTICATED_SEARCH_PRODUCTS
      : UNAUTHENTICATED_SEARCH_PRODUCTS;

    const RES_KEY = isSemanticProductSearch
      ? ProductOperation.SemanticProductSearch
      : isLoggedIn
      ? ProductOperation.AuthenticatedSearch
      : ProductOperation.UnauthenticatedSearch;

    // Default values
    const page = request.page ?? { start: 0, size: 20 };
    const computeFacets = request.computeFacets ?? false;
    const skipAvailabilityCheck = request.skipAvailabilityCheck ?? false;
    const applySpellingCheck = request.applySpellingCheck ?? true;

    // Build GraphQL variables
    const variables: any = {
      page,
      computeFacets,
      skipAvailabilityCheck,
      applySpellingCheck,
      facetFieldNames: request.facetFieldNames ?? [],
      sort: request.sort ?? [],
    };

    // Add filters if provided
    if (request.filters) {
      const filters = request.filters;
      if (filters.titleInclude) variables.titleInclude = filters.titleInclude;
      if (filters.titleExclude) variables.titleExclude = filters.titleExclude;
      if (filters.stacklineSkus)
        variables.stacklineSkus = filters.stacklineSkus;
      if (filters.stacklineSkusExclude)
        variables.stacklineSkusExclude = filters.stacklineSkusExclude;
      if (filters.campaignTypes)
        variables.campaignTypes = filters.campaignTypes;
      if (filters.retailPrice) variables.retailPrice = filters.retailPrice;
      if (filters.reviewsRating)
        variables.reviewsRating = filters.reviewsRating;
      if (filters.reviewsCount) variables.reviewsCount = filters.reviewsCount;
      if (filters.brandIds) variables.brandIds = filters.brandIds;
      if (filters.departmentIds)
        variables.departmentIds = filters.departmentIds;
      if (filters.categoryIds) variables.categoryIds = filters.categoryIds;
      if (filters.subCategoryIds)
        variables.subCategoryIds = filters.subCategoryIds;
      if (filters.retailerIds) variables.retailerIds = filters.retailerIds;
      if (filters.isBrandClubReady !== undefined)
        variables.isBrandClubReady = filters.isBrandClubReady;
      if (filters.totalRewardAmount)
        variables.totalRewardAmount = filters.totalRewardAmount;
      if (filters.imageData) variables.imageData = filters.imageData;
      if (filters.imageMimeType) variables.imageMimeType = filters.imageMimeType;
    }

    // Add treatment if provided
    if (request.treatment) {
      variables.treatment = request.treatment;
    }

    const operationName = `${RES_KEY}-${
      request.filters?.titleInclude?.join(",") || "search"
    }`;

    const data = await graphqlFetch({
      operationName,
      query: SearchQuery,
      variables,
      signal,
    });

    const searchResult = data?.data?.[RES_KEY];

    return {
      products: searchResult?.products,
      totalProducts: searchResult?.totalProducts,
      facets: searchResult?.facets,
      suggestedTerm: searchResult?.suggestedTerm,
      hasMore: searchResult?.products?.length === page.size,
      summaryText: searchResult?.summaryText,
    };
  } catch (error) {
    console.error("Error searching products:", error);
    throw new Error("Error searching products");
  }
};

/**
 * Convenience method for simple text-based product search
 * @param searchText The text to search for
 * @param options Additional search options
 * @param signal Optional AbortSignal for request cancellation
 * @returns Promise with product search results
 */
export const searchProductsByText = async (
  searchText: string,
  options: {
    brandIds?: number[];
    page?: { start: number; size: number };
    isLoggedIn?: boolean;
    sort?: Array<{ fieldName: string; direction: "asc" | "desc" }>;
  } = {},
  isSemanticProductSearch: boolean = false,
  signal?: AbortSignal
): Promise<ProductSearchResponse> => {
  const request: ProductSearchRequest = {
    filters: {
      titleInclude: [searchText],
      ...(options.brandIds && { brandIds: options.brandIds }),
    },
    page: options.page ?? { start: 0, size: 20 },
    sort: options.sort ?? [],
  };

  return searchProducts(
    request,
    options.isLoggedIn ?? false,
    isSemanticProductSearch,
    signal
  );
};

/**
 * Searches for products using image data with a text prompt
 * @param imageData Base64 encoded image data
 * @param imageMimeType MIME type of the image
 * @param textPrompt Text prompt to accompany the image
 * @param options Additional search options
 * @param signal Optional AbortSignal for request cancellation
 * @returns Promise with product search results
 */
export const searchProductsByImageWithPrompt = async (
  imageData: string,
  imageMimeType: string,
  textPrompt: string,
  options: {
    brandIds?: number[];
    page?: { start: number; size: number };
    isLoggedIn?: boolean;
  } = {},
  signal?: AbortSignal
): Promise<ProductSearchResponse> => {
  const request: ProductSearchRequest = {
    filters: {
      titleInclude: [textPrompt], // Include the text prompt
      imageData,
      imageMimeType,
      ...(options.brandIds && { brandIds: options.brandIds }),
    },
    page: options.page ?? { start: 0, size: 20 },
  };

  return searchProducts(
    request,
    options.isLoggedIn ?? false,
    true, // Always use semantic search for image searches
    signal
  );
};

/**
 * Fetches recently searched terms
 * @param signal Optional AbortSignal for request cancellation
 * @returns Promise with recently searched terms
 */
export const fetchRecentlySearchedTerms = async (
  signal?: AbortSignal
): Promise<RecentlySearchedTerm[]> => {
  const headers = {
    ...(await getHeadersWithUniqueVisitorId()),
    ...getCommonRequestHeaders(),
  };

  const res = await fetch(DOMAIN + `/search/recentSearches`, {
    method: "GET",
    headers,
    signal,
  });

  if (res.ok) {
    const response = await res.json();
    return response.data?.payload || [];
  } else {
    throw new Error("Error fetching recently searched terms");
  }
};
